import type { ShiftStateResult, ShiftStateView } from '@didto/domain';
import type { WriteOffRowKind } from '@didto/domain';
import { useQuery } from '@tanstack/react-query';
import type { Row } from './db';
import { useDeviceStore } from './deviceStore';
import { topicPos, useRealtimeInvalidate } from './realtime';
import { supabase } from './supabase';

/**
 * Shifts and cash (Phase 7A, ADR-024). A tablet reads its drawer's shift through shift_state,
 * which never carries expected cash or count amounts (blind counts). The owner reads shift rows
 * directly in a browser (shift.review). Every write is a command.
 */
export const shiftKeys = {
  state: (terminalId: string, token: string | null) =>
    ['shift-state', terminalId, token ?? ''] as const,
  all: ['shift-state'] as const,
  summary: (shiftId: string) => ['shift-summary', shiftId] as const,
  drawers: (locationId: string) => ['cash-drawers', locationId] as const,
  openShifts: (locationId: string) => ['open-shifts', locationId] as const,
};

export async function fetchShiftState(): Promise<ShiftStateResult> {
  const token = useDeviceStore.getState().sessionToken;
  const { data, error } = await supabase.rpc('shift_state', {
    p_session_token: token ?? undefined,
  });
  if (error) throw error;
  return data as unknown as ShiftStateResult;
}

/** The tablet's drawer and shift; refetched when any shift of the location changes. */
export function useShiftState(enabled: boolean, terminalId: string, locationId: string) {
  const token = useDeviceStore((s) => s.sessionToken);
  useRealtimeInvalidate(enabled && locationId ? topicPos(locationId) : null, [[...shiftKeys.all]]);
  return useQuery({
    queryKey: shiftKeys.state(terminalId, token),
    enabled: enabled && !!terminalId,
    queryFn: async (): Promise<ShiftStateView | null> => {
      const r = await fetchShiftState();
      return r.ok ? r : null;
    },
    refetchInterval: 30_000,
    staleTime: 5_000,
  });
}

export interface ShiftSummary {
  shift: {
    id: string;
    seq: number;
    status: string;
    business_date: string;
    drawer: { id: string; name: string } | null;
    cashier: { id: string; name: string };
    opened_at: string;
    open_kind: string;
    closing_started_at: string | null;
    closed_at: string | null;
    close_kind: string | null;
    closed_by: { id: string; name: string } | null;
  };
  cash: {
    cash_sales_minor: number;
    change_given_minor: number;
    cash_returned_minor: number;
    pay_ins_minor: number;
    pay_outs_minor: number;
    pay_outs_by_category: Record<string, number>;
    safe_drops_minor: number;
    entries: {
      pay_ins: number;
      pay_outs: number;
      safe_drops: number;
      no_sales: number;
      voided: number;
    };
    expected_minor: number;
    late_entries: { count: number; amount_minor: number };
    opening_count_minor: number;
    expected_opening_minor: number | null;
    opening_difference_minor: number | null;
    counted_minor: number | null;
    close_expected_minor: number | null;
    over_short_minor: number | null;
    retained_minor: number | null;
    removed_minor: number | null;
    removed_to: 'owner' | 'safe' | null;
    variance_reason: string | null;
    /** The count tolerance the shift was closed under (differences within it are not red). */
    tolerance_minor?: number;
  };
  methods: {
    method: string;
    kind: string;
    count: number;
    captured_minor: number;
    voided_minor: number;
    refunded_minor: number;
    net_minor: number;
  }[];
  counts: {
    purpose: 'opening' | 'closing';
    attempt: number;
    final: boolean;
    within_tolerance: boolean;
    counted_minor: number;
    difference_minor: number | null;
    by: string;
    at: string;
  }[];
  orders: {
    placed: number;
    voided: number;
    lines_voided: number;
    open_unpaid: number;
    served_unpaid: number;
  };
  corrections: {
    action: string;
    entity_id?: string | null;
    by: string | null;
    approver: string | null;
    reason: string | null;
    amount_minor: number | null;
    at: string;
  }[];
  others: {
    action: string;
    by: string | null;
    method?: string | null;
    category?: string | null;
    amount_minor: number;
    at: string;
  }[];
  discarded: {
    original_kind: string;
    amount_minor: number | null;
    method: string | null;
    reason: string;
    by: string;
    at: string;
  }[];
  /** ADR-031: present only when the shift has a write-off (no cash, no method total). */
  write_offs?: {
    by_kind: Partial<Record<WriteOffRowKind, { count: number; total_minor: number }>>;
    owner_approved: number;
    items: {
      id: string;
      order_id: string;
      order_number: string;
      kind: WriteOffRowKind;
      amount_minor: number;
      authority: 'cashier' | 'owner';
      reason: string;
      employee: string | null;
      by: string | null;
      approver: string | null;
      reinstates_id: string | null;
      reinstated: boolean;
      at: string;
    }[];
  };
  /** ADR-032: present only when the shift recorded a return of a served item. */
  returns?: {
    count: number;
    total_minor: number;
    refunded_minor: number;
    owner_approved: number;
    items: {
      id: string;
      order_id: string;
      order_number: string;
      line: string;
      qty: number;
      amount_minor: number;
      refunded_minor: number;
      authority: 'cashier' | 'owner';
      reason: string;
      by: string | null;
      approver: string | null;
      at: string;
    }[];
  };
  /** ADR-032 (D5): payments whose reference was used before, with the reason the cashier gave. */
  reference_flags?: {
    payment_id: string;
    order_number: string;
    amount_minor: number;
    method: string;
    reference_last4: string;
    source: 'counter' | 'queued' | 'owner_entry';
    reason: string;
    matches: number;
    /** Sent late with a reference that does not match the method's format (flagged, not refused). */
    format_mismatch: boolean;
    by: string | null;
    at: string;
  }[];
}

/** The frozen summary of a closed shift and its figures now (late entries included). */
export async function fetchShiftSummary(
  shiftId: string,
): Promise<{ frozen: ShiftSummary | null; live: ShiftSummary }> {
  const token = useDeviceStore.getState().sessionToken;
  const { data, error } = await supabase.rpc('shift_summary', {
    p_shift_id: shiftId,
    p_session_token: token ?? undefined,
  });
  if (error) throw error;
  const v = data as unknown as { frozen: ShiftSummary | null; live: ShiftSummary };
  return v;
}

export type CashDrawer = Pick<
  Row<'cash_drawer'>,
  'id' | 'location_id' | 'name' | 'is_main' | 'archived_at'
>;

export async function fetchDrawers(locationId: string): Promise<CashDrawer[]> {
  const { data, error } = await supabase
    .from('cash_drawer')
    .select('id, location_id, name, is_main, archived_at')
    .eq('location_id', locationId)
    .order('is_main', { ascending: false })
    .order('name');
  if (error) throw error;
  return data ?? [];
}

export interface OpenShiftRow {
  id: string;
  drawer_id: string;
  status: string;
  cashier_employee_id: string;
  opened_at: string;
}

/** Shifts open or being counted at a location (the owner's account: shift.review). */
export async function fetchOpenShifts(locationId: string): Promise<OpenShiftRow[]> {
  const { data, error } = await supabase
    .from('shift')
    .select('id, drawer_id, status, cashier_employee_id, opened_at')
    .eq('location_id', locationId)
    .neq('status', 'closed');
  if (error) throw error;
  return (data ?? []) as OpenShiftRow[];
}
